import * as yup from 'yup';
import FormFieldType from 'src/shared/ui/components/Form/FormFieldFactory/FormFieldType';
import { t } from 'src/shared/literals';

const getDefaultMoneyCollectionEmployeeId = ({ fields, employeeList }) => {
  if (fields && fields.moneyCollectionEmployeeId) {
    return fields.moneyCollectionEmployeeId;
  } else {
    const defaultEmployeeList = employeeList.filter(
      (x) => x.userId === JSON.parse(localStorage.userHeadquarterList)[0].userId
    );
    const defaultEmployeeId =
      defaultEmployeeList && defaultEmployeeList.length
        ? defaultEmployeeList[0].id
        : '';

    return defaultEmployeeId;
  }
};

const getDefaultCustomerContactId = ({
  fields,
  customerContactList,
  invoiceBillToCustomerContactId
}) => {
  if (fields && fields.customerContactId) {
    return fields.customerContactId;
  } else {
    if (customerContactList && customerContactList.length) {
      const customerContact = customerContactList.filter(
        (x) => x.id === invoiceBillToCustomerContactId
      );

      return customerContact.length ? customerContact[0].id : null;
    } else {
      return '';
    }
  }
};

const getBalanceByCustomerContactId = ({
  fields,
  customerContactList,
  invoiceBillToCustomerContactId
}) => {
  if (customerContactList && customerContactList.length) {
    const customerContact = customerContactList.filter(
      (x) => x.id === invoiceBillToCustomerContactId
    );

    return customerContact.length ? customerContact[0].balance : 0;
  } else {
    return 0;
  }
};

const getPaymentTypeIdByCode = (code, paymentTypeList) =>
  paymentTypeList.filter((paymentType) => paymentType.code === code)[0].id;

const getPaymentTypeCodeById = (paymentTypeId, paymentTypeList) =>
  paymentTypeList.filter((paymentType) => paymentType.id === paymentTypeId)[0]
    .code;

const isValidationStatusByPaymentTypeId = (paymentTypeId, paymentTypeList) => {
  const paymentCode = getPaymentTypeCodeById(paymentTypeId, paymentTypeList);

  console.log(paymentTypeId, paymentCode);

  return ['TIGO_TRANSFER', 'BANK_TRANSFER', 'BANK_CHECK'].includes(paymentCode);
};

const Payment = ({
  fields,
  user,
  paymentTypeList,
  employeeList,
  customerContactList,
  invoiceBillToCustomerContactId
}) => [
  {
    name: 'invoiceStampingNumber',
    validation: yup.string().required('fieldRequired'),
    type: FormFieldType.SELECT,
    value: '001-002',
    options: [{ id: '001-002', name: '001-002' }],
    readonly: fields && fields.id
  },
  {
    name: 'invoiceNumber',
    validation: yup
      .number()
      .typeError('fieldNumeric')
      .required('fieldRequired'),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.invoiceNumber ? fields.invoiceNumber : '',
    readonly: fields && fields.id
  },
  {
    name: 'paymentTypeId',
    validation: yup
      .string()
      .required('fieldRequired')
      .nullable(false),
    type: FormFieldType.SELECT,
    value:
      fields && fields.paymentTypeId
        ? fields.paymentTypeId
        : getPaymentTypeIdByCode('CASH_MONEY_COLLECTOR', paymentTypeList),
    options: paymentTypeList.map((x) => {
      return { id: x.id, name: x.code };
    }),
    fullWidth: true,
    readonly: fields && fields.id
  },
  {
    name: 'validationStatus',
    validation: yup.string().nullable(true),
    type: FormFieldType.SELECT,
    value:
      fields && fields.id
        ? fields.validationStatus || ''
        : fields &&
          fields.paymentTypeId &&
          isValidationStatusByPaymentTypeId(
            fields.paymentTypeId,
            paymentTypeList
          )
        ? 'pending'
        : '',
    options:
      fields && fields.id
        ? [
            { id: 'pending', name: 'pending' },
            { id: 'validated', name: 'validated' }
          ]
        : [{ id: 'pending', name: 'pending' }],
    fullWidth: true,
    enabled:
      fields && fields.paymentTypeId
        ? isValidationStatusByPaymentTypeId(
            fields.paymentTypeId,
            paymentTypeList
          )
        : false,
    readonly: fields && fields.validationStatus === 'validated'
  },
  {
    name: 'promiseDate',
    validation: yup.date().required('fieldRequired'),
    type: FormFieldType.DATE,
    value: fields && fields.promiseDate ? fields.promiseDate : new Date(),
    enabled:
      fields && fields.paymentTypeId
        ? paymentTypeList.filter(
            (x) => x.id === fields.paymentTypeId && ['PROMISE'].includes(x.code)
          ).length
        : false,
    readonly: fields && fields.id
  },
  {
    name: 'balance',
    validation: yup.number(),
    type: FormFieldType.TEXT_FIELD,
    value: getBalanceByCustomerContactId({
      fields,
      customerContactList,
      invoiceBillToCustomerContactId:
        fields?.customerContactId || invoiceBillToCustomerContactId
    }),
    enabled:
      fields && fields.paymentTypeId
        ? paymentTypeList.filter(
            (x) =>
              x.id === fields.paymentTypeId && !['PROMISE'].includes(x.code)
          ).length
        : true,
    readonly: true
  },
  {
    name: 'amount',
    validation: yup
      .number()
      .typeError('fieldNumeric')
      .required('fieldRequired'),
    type: FormFieldType.TEXT_FIELD,
    value:
      fields && fields.amount
        ? fields.amount -
          getBalanceByCustomerContactId({
            fields,
            customerContactList,
            invoiceBillToCustomerContactId:
              fields?.customerContactId || invoiceBillToCustomerContactId
          })
        : '',
    enabled:
      fields && fields.paymentTypeId
        ? paymentTypeList.filter(
            (x) =>
              x.id === fields.paymentTypeId && !['PROMISE'].includes(x.code)
          ).length
        : true,
    readonly: fields?.id && fields.validationStatus !== 'pending'
  },
  {
    name: 'customerContactId',
    validation: yup
      .string()
      .nullable(false)
      .required('fieldRequired'),
    type: FormFieldType.SELECT,
    value: getDefaultCustomerContactId({
      fields,
      customerContactList,
      invoiceBillToCustomerContactId:
        fields?.customerContactId || invoiceBillToCustomerContactId
    }),
    options: customerContactList
      .map((x) => {
        return {
          id: x.id,
          name: `${x.firstname} ${x.surnames} ${x.document} (${t(
            x.relationship
          )})`
        };
      })
      .filter((x) => x),
    fullWidth: true,
    enabled:
      fields && fields.paymentTypeId
        ? paymentTypeList.filter(
            (x) =>
              x.id === fields.paymentTypeId &&
              !['PROMISE', 'CREDIT_NOTE'].includes(x.code)
          ).length
        : true,
    readonly: fields && fields.id
  },
  {
    name: 'moneyCollectionEmployeeId',
    validation: yup
      .string()
      .nullable(false)
      .required('fieldRequired'),
    type: FormFieldType.SELECT,
    value: getDefaultMoneyCollectionEmployeeId({ fields, employeeList }),
    options: employeeList
      .map((x) => {
        return {
          id: x.id,
          name: `${x.firstname} ${x.surnames} ${x.document}`
        };
      })
      .filter((x) =>
        user && user.scopes.includes('admin:payments')
          ? x
          : x.id ===
            getDefaultMoneyCollectionEmployeeId({
              fields,
              employeeList
            })
      ),
    fullWidth: true,
    enabled:
      fields && fields.paymentTypeId
        ? paymentTypeList.filter(
            (x) =>
              x.id === fields.paymentTypeId &&
              !['PROMISE', 'CREDIT_NOTE'].includes(x.code)
          ).length
        : true,
    readonly: fields && fields.id
  },
  {
    name: 'reference',
    validation: yup.string(),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.reference ? fields.reference : '',
    readonly: fields?.id && fields.validationStatus !== 'pending'
  },
  {
    name: 'comments',
    validation: yup.string(),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.comments ? fields.comments : '',
    fullWidth: true,
    readonly: fields && fields.id
  },
  {
    name: 'internalComments',
    validation: yup.string(),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.internalComments ? fields.internalComments : '',
    fullWidth: true,
    readonly: true
  },
  {
    name: 'newComment',
    validation: yup.string().nullable(true),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.newComment ? fields.newComment : '',
    fullWidth: true
  }
];

export default Payment;
